import { useState } from 'react';
import { addDays, format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { Bar, BarChart, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// Mock data
const overallResults = [
  { name: 'Booked', value: 87000 },
  { name: 'Not Booked', value: 213000 },
];

const notBookedReasons = [
  { reason: 'Not Interested', count: 95000 },
  { reason: 'Already Booked', count: 45000 },
  { reason: 'No Answer', count: 40000 },
  { reason: 'Wrong Number', count: 20000 },
  { reason: 'Other', count: 13000 },
];

const bookingRateOverTime = [
  { date: '2023-05-01', rate: 25 },
  { date: '2023-05-02', rate: 28 },
  { date: '2023-05-03', rate: 32 },
  { date: '2023-05-04', rate: 35 },
  { date: '2023-05-05', rate: 30 },
  { date: '2023-05-06', rate: 34 },
  { date: '2023-05-07', rate: 38 },
];

const detailedResults = [
  { date: '2023-05-01', calls: 42000, booked: 10500, notBooked: 31500, bookingRate: '25%' },
  { date: '2023-05-02', calls: 43000, booked: 12040, notBooked: 30960, bookingRate: '28%' },
  { date: '2023-05-03', calls: 44000, booked: 14080, notBooked: 29920, bookingRate: '32%' },
  { date: '2023-05-04', calls: 43500, booked: 15225, notBooked: 28275, bookingRate: '35%' },
  { date: '2023-05-05', calls: 42500, booked: 12750, notBooked: 29750, bookingRate: '30%' },
  { date: '2023-05-06', calls: 42000, booked: 14280, notBooked: 27720, bookingRate: '34%' },
  { date: '2023-05-07', calls: 43000, booked: 16340, notBooked: 26660, bookingRate: '38%' },
];

const Outcome = () => {
  const [date, setDate] = useState<DateRange | undefined>({
    from: addDays(new Date(), -7),
    to: new Date(),
  });

  return (
    <div className="p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Phone Campaign Dashboard</h1>
        <Popover>
          <PopoverTrigger asChild>
            <Button id="date" variant={'outline'} className={`w-[300px] justify-start text-left font-normal`}>
              <CalendarIcon className="w-4 h-4 mr-2" />
              {date?.from ? (
                date.to ? (
                  <>
                    {format(date.from, 'LLL dd, y')} - {format(date.to, 'LLL dd, y')}
                  </>
                ) : (
                  format(date.from, 'LLL dd, y')
                )
              ) : (
                <span>Pick a date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={setDate}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">300,000</div>
            <p className="text-xs text-muted-foreground">+2.1% from last week</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Appointments Booked</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">87,000</div>
            <p className="text-xs text-muted-foreground">+5.4% from last week</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Booking Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">29%</div>
            <p className="text-xs text-muted-foreground">+1.2% from last week</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Average Call Duration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">3m 24s</div>
            <p className="text-xs text-muted-foreground">-12s from last week</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>Booking Rate Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                rate: {
                  label: 'Booking Rate',
                  color: 'hsl(var(--chart-1))',
                },
              }}
              className="h-[300px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={bookingRateOverTime}>
                  <XAxis dataKey="date" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Line type="monotone" dataKey="rate" stroke="var(--color-rate)" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>Overall Results</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                value: {
                  label: 'Value',
                  color: 'hsl(var(--chart-1))',
                },
              }}
              className="h-[300px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={overallResults}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {overallResults.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === 0 ? 'var(--color-booked)' : 'var(--color-notBooked)'}
                      />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Reasons for Not Booking</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              count: {
                label: 'Count',
                color: 'hsl(var(--chart-1))',
              },
            }}
            className="h-[300px]"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={notBookedReasons}>
                <XAxis dataKey="reason" />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="count" fill="var(--color-count)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Detailed Results</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Total Calls</TableHead>
                <TableHead>Booked</TableHead>
                <TableHead>Not Booked</TableHead>
                <TableHead>Booking Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detailedResults.map((row) => (
                <TableRow key={row.date}>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.calls.toLocaleString()}</TableCell>
                  <TableCell>{row.booked.toLocaleString()}</TableCell>
                  <TableCell>{row.notBooked.toLocaleString()}</TableCell>
                  <TableCell>{row.bookingRate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Outcome;
